import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom'
import { ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react'
import toast from 'react-hot-toast'
import authService from '@/services/authService'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!token) {
      toast.error('Enlace de restablecimiento inválido o expirado')
      navigate('/forgot-password')
    }
  }, [token, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden')
      return
    }
    if (newPassword.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres')
      return
    }
    setIsLoading(true)
    try {
      await authService.resetPassword(token, newPassword)
      setDone(true)
      toast.success('Contraseña actualizada')
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error?.message ||
          'Error al restablecer la contraseña'
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8 pt-safe pb-safe">
      <div className="w-full max-w-md">
        <Card className="p-6 sm:p-8 shadow-md">
          <RouterLink
            to="/login"
            className="inline-flex items-center gap-1.5 text-body-sm text-subtle hover:text-primary transition-colors mb-4"
          >
            <ArrowLeft size={14} /> Volver al login
          </RouterLink>

          <h1 className="font-display text-h1 font-semibold text-foreground leading-tight">
            Nueva contraseña
          </h1>

          {!done ? (
            <>
              <p className="text-body-sm text-subtle mt-1 mb-5">
                Ingresa tu nueva contraseña. Mínimo 8 caracteres.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="rp-pass">Nueva contraseña</Label>
                  <Input
                    id="rp-pass"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rp-confirm">Confirmar contraseña</Label>
                  <Input
                    id="rp-confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>
                <Button type="submit" size="cta" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Actualizando…
                    </>
                  ) : (
                    'Restablecer contraseña'
                  )}
                </Button>
              </form>
            </>
          ) : (
            <Alert variant="success" className="mt-4">
              <CheckCircle2 size={16} />
              <AlertDescription>
                Tu contraseña fue actualizada.{' '}
                <RouterLink to="/login" className="font-medium underline">
                  Iniciar sesión
                </RouterLink>
              </AlertDescription>
            </Alert>
          )}
        </Card>
      </div>
    </div>
  )
}
